import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  fetchOverview,
  fetchProgrammes,
  fetchRecentExtractions,
  fetchApercuMasse,
  materializeExtracted,
  runExtractionWindow,
  type CurriculumOverview,
  type ExtractionRow,
  type ApercuMasse,
  type ProgrammeRow,
} from '../lib/curriculum';
import { Alert } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';


export default function AdminCurriculum() {
  const isAdminViewer = useIsAdminViewer();
  const [ov, setOv] = useState<CurriculumOverview | null>(null);
  const [extractions, setExtractions] = useState<ExtractionRow[]>([]);
  const [programmes, setProgrammes] = useState<ProgrammeRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [apercu, setApercu] = useState<ApercuMasse | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setError(null);
    try {
      const [o, e, p, a] = await Promise.all([
        fetchOverview(),
        fetchRecentExtractions(40),
        fetchProgrammes(50),
        fetchApercuMasse(),
      ]);
      setOv(o);
      setExtractions(e);
      setProgrammes(p);
      setApercu(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const materialize = async () => {
    setBusy(true);
    setError(null);
    setLastRun(null);
    try {
      const r = await materializeExtracted();
      /* Un refus du garde rend processed = 0. Sans ce test, le message dirait
         « rien à matérialiser, tous les docs ont déjà un programme » — ce qui
         est FAUX et rassurant : 29 documents viennent d'être refusés. Un garde
         dont le refus s'affiche comme un succès ne protège de rien. */
      if (r.refuses) {
        setError(r.motif ?? `${r.refuses} document(s) refusés par le garde de matérialisation.`);
        return;
      }
      setLastRun(
        r.processed === 0
          ? 'Rien à matérialiser (tous les docs extraits ont déjà un programme).'
          : `🔨 ${r.processed} doc(s) matérialisé(s) · ${r.themes} thèmes · ${r.items} items` +
            (r.skipped ? ` · ${r.skipped} ignoré(s) (matière non résolue)` : ''),
      );
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const runNext = async () => {
    setBusy(true);
    setError(null);
    setLastRun(null);
    try {
      const r = await runExtractionWindow();
      if (r.error) {
        setError(`${r.error}${r.detail ? ` — ${r.detail}` : ''}`);
      } else if (r.message && !r.page_start) {
        setLastRun(r.message);
      } else {
        setLastRun(
          `📄 ${r.title ?? r.document_id} · pages ${r.page_start}–${r.page_end} · ` +
            `${r.subject_code ?? '—'}/${r.cycle_code ?? '—'} · ${r.item_count ?? 0} items` +
            (r.more_windows ? ' · (fenêtres suivantes restantes)' : ' · ✅ doc terminé'),
        );
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const pct = ov && ov.totalDocs > 0
    ? Math.round(((ov.byStatus.extracted + ov.byStatus.skipped) / ov.totalDocs) * 100)
    : null;

  return (
    <>
      <HeaderContent
        width="xl"
        showBurger
        backTo="/admin"
        backLabel="Admin"
        denseActions
        actions={
          <div className="flex gap-2">
            <Link to="/admin/curriculum/ref" className={buttonVariants({ variant: 'outline', size: 'sm' })}>⚙ Référentiel</Link>
            {isAdminViewer && (
              <>
                <Button size="sm" onClick={runNext} disabled={busy} className="shadow-card">
                  {busy ? '⏳…' : '▶ Extraire la fenêtre suivante'}
                </Button>
                {/* Le bouton annonçait « Matérialiser (29) » quand le garde refusait
                    les 29 : l'opérateur ne l'apprenait qu'après avoir cliqué. Il
                    annonce désormais ce qui passerait vraiment. */}
                <Button size="sm" onClick={materialize} disabled={busy || !apercu || apercu.autorises === 0}
                  className={cn("bg-naia-green text-card shadow-card", apercu && apercu.autorises === 0 && "opacity-50")}>
                  🔨 Matérialiser{apercu && apercu.autorises > 0 ? ` (${apercu.autorises})` : ''}
                </Button>
              </>
            )}
          </div>
        }
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Programmes structurés</h1>
          <span className={HEADER_BADGE_CLASS}>EDU-66</span>
        </div>
      </HeaderContent>
      <main className="max-w-[1100px] mx-auto px-4 pb-12 pt-4">
        {error && <Alert>{error}</Alert>}
        {lastRun && <Alert>{lastRun}</Alert>}

        <div className="mt-[14px] grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-2">
          <Kpi loading={loading} label="Documents traités" value={ov ? `${ov.byStatus.extracted + ov.byStatus.skipped}/${ov.totalDocs}` : '—'} sub={pct === null ? undefined : `${pct}%`} accent="var(--naia-info-bg)" />
          <Kpi loading={loading} label="Programmes" value={ov ? String(ov.programmes) : '—'} accent="var(--naia-card-alt)" />
          <Kpi loading={loading} label="À matérialiser" value={apercu ? String(apercu.autorises) : '—'} sub={apercu ? `sur ${apercu.eligibles}` : undefined} accent={apercu && apercu.autorises > 0 ? 'var(--naia-info-bg)' : undefined} />
          <Kpi loading={loading} label="Refusés par le garde" value={apercu ? String(apercu.bloques) : '—'} accent={apercu && apercu.bloques > 0 ? 'var(--naia-info-bg)' : undefined} />
          <Kpi loading={loading} label="Non jugeables" value={apercu ? String(apercu.non_jugeables) : '—'} />
          <Kpi loading={loading} label="Thèmes" value={ov ? String(ov.themes) : '—'} />
          <Kpi loading={loading} label="Items" value={ov ? String(ov.items) : '—'} accent="var(--naia-card-alt)" />
          <Kpi loading={loading} label="Fenêtres extraites" value={ov ? String(ov.windows) : '—'} />
          <Kpi loading={loading} label="En attente" value={ov ? String(ov.byStatus.pending) : '—'} />
          <Kpi loading={loading} label="Ignorés" value={ov ? String(ov.byStatus.skipped) : '—'} />
          <Kpi loading={loading} label="Erreurs" value={ov ? String(ov.byStatus.error) : '—'} accent={ov && ov.byStatus.error > 0 ? 'var(--naia-info-bg)' : undefined} />
        </div>
        <p className="text-[11px] text-muted-foreground mt-[6px]">
          Référentiel : {ov?.cycles ?? '—'} cycles · {ov?.subjects ?? '—'} disciplines · {ov?.levels ?? '—'} niveaux.
          Extraction par fenêtre glissante (8 p, overlap 2) ; payloads bruts persistés.
        </p>

        <SectionTitle>FRICTIONS DE SCHÉMA · récentes (challenge du modèle)</SectionTitle>
        <div className="bg-card border border-border rounded-[12px] shadow-card overflow-hidden">
          {loading ? (
            <SkeletonRows />
          ) : extractions.filter((e) => e.schema_friction).length === 0 ? (
            <p className="p-3 text-muted-foreground text-[13px]">Aucune friction remontée pour l'instant.</p>
          ) : (
            extractions.filter((e) => e.schema_friction).slice(0, 12).map((e, i) => (
              <div key={`${e.document_id}-${e.page_start}-${i}`} className="px-3 py-2 border-b border-naia-line-soft">
                <div className="text-[11px] text-muted-foreground font-mono">
                  {e.title ?? e.document_id} · p. {e.page_start}–{e.page_end}
                </div>
                <div className="text-[13px] mt-0.5">{e.schema_friction}</div>
              </div>
            ))
          )}
        </div>

        <SectionTitle>PROGRAMMES MATÉRIALISÉS</SectionTitle>
        <div className="border border-border rounded-[12px] overflow-x-auto bg-card">
          <Row header cols={['MATIÈRE / DOCUMENT', 'CYCLE', 'VOIE', 'THÈMES', 'ITEMS', 'STATUT']} gridClass="grid-cols-[minmax(0,1fr)_90px_80px_60px_56px_80px]" />
          {loading ? (
            <SkeletonRows />
          ) : programmes.length === 0 ? (
            <p className="p-3 text-muted-foreground text-[13px]">Aucun programme matérialisé.</p>
          ) : (
            programmes.map((p) => (
              <Row
                key={p.id}
                gridClass="grid-cols-[minmax(0,1fr)_90px_80px_60px_56px_80px]"
                cols={[
                  <Link to={`/admin/curriculum/p/${p.id}`} className="min-w-0 no-underline text-inherit block">
                    <div className="font-bold text-[13px] text-foreground">✏️ {p.subject ?? '— matière ?'}</div>
                    <div className="text-[10px] text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">{p.doc_title}</div>
                  </Link>,
                  p.cycle ?? '—',
                  p.voie ?? '—',
                  String(p.theme_count),
                  String(p.item_count),
                  p.status,
                ]}
              />
            ))
          )}
        </div>

        <SectionTitle>FENÊTRES EXTRAITES · récentes</SectionTitle>
        <div className="border border-border rounded-[12px] overflow-x-auto bg-card">
          <Row header cols={['DOCUMENT', 'PAGES', 'ITEMS', 'STATUT']} gridClass="grid-cols-[minmax(0,1fr)_80px_56px_70px]" />
          {loading ? (
            <SkeletonRows />
          ) : extractions.length === 0 ? (
            <p className="p-3 text-muted-foreground text-[13px]">Aucune fenêtre extraite.</p>
          ) : (
            extractions.slice(0, 25).map((e, i) => (
              <Row
                key={`${e.document_id}-${e.page_start}-${i}`}
                gridClass="grid-cols-[minmax(0,1fr)_80px_56px_70px]"
                cols={[
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px]">{e.title ?? e.document_id}</span>,
                  `${e.page_start}–${e.page_end}`,
                  String(e.item_count),
                  e.status,
                ]}
              />
            ))
          )}
        </div>
      </main>
    </>
  );
}

function Row({ cols, header, gridClass }: { cols: React.ReactNode[]; header?: boolean; gridClass: string }) {
  return (
    <div className={cn(
      "grid gap-2 min-w-[480px] px-[10px] py-[6px] items-center border-b border-naia-line-soft text-[12px] text-foreground",
      gridClass,
      header && "border-b border-border bg-secondary text-[10px] font-black tracking-[0.6px] text-muted-foreground"
    )}>
      {cols.map((c, i) => (
        <div key={i} className={cn("min-w-0", i === 0 ? "text-left font-[inherit]" : "text-right font-mono")}>{c}</div>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, accent, loading }: { label: string; value: string; sub?: string; accent?: string; loading?: boolean }) {
  const accentStyle = accent ? ({ '--kpi-accent': accent } as CSSProperties) : undefined;
  return (
    <div className="bg-[var(--kpi-accent,var(--naia-card))] border border-border rounded-[12px] px-[10px] py-2 shadow-card" style={accentStyle}>
      <div className="text-[9px] font-black tracking-[0.9px] text-muted-foreground uppercase">{label}</div>
      <div className="text-[18px] font-extrabold mt-0.5">
        {loading ? <Skeleton className="h-5 w-12" /> : value}
        {!loading && sub && <span className="text-[11px] font-bold text-muted-foreground ml-1">{sub}</span>}
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="p-3 flex flex-col gap-2" aria-busy="true" aria-label="Chargement">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-5 mb-[6px] text-[11px] font-black tracking-[1.2px]">{children}</h2>;
}
